import { IAction, NetworkAction } from './';
import { ExtractType } from './extractors';
import { Entity, NetworkArray } from 'typed/api/entities';

export type PartialEntity<E extends Entity> = Entity & Partial<E>;

export interface IEntityActionSet<T extends string, E extends Entity> {
    loadAll: NetworkAction<T, void, NetworkArray<E>>;
    load: NetworkAction<T, { entityID: E['ID']; }, E>;
    addAll: NetworkAction<T, { entities: PartialEntity<E>[]; }, void | number[]>;
    add: NetworkAction<T, { entity: PartialEntity<E>; }, void | number | E>;
    editAll: NetworkAction<T, { entities: PartialEntity<E>[]; }, void>;
    edit: NetworkAction<T, { entity: PartialEntity<E>; }, void | E>;
    deleteAll: NetworkAction<T, { entityIDs: E['ID'][]; }, void>;
    delete: NetworkAction<T, { entityID: E['ID']; }, void>;
    voidAll: IAction<T, { immediately: boolean; }>;
    void: IAction<T, { immediately: boolean, entityID: E['ID']; }>;
}

export type IEntityActionCreatorSet<T extends string, E extends Entity> = {
    loadAll(...args: any[]): IEntityActionSet<T, E>['loadAll'];
    load(...args: any[]): IEntityActionSet<T, E>['load'];
    addAll(...args: any[]): IEntityActionSet<T, E>['addAll'];
    add(...args: any[]): IEntityActionSet<T, E>['add'];
    editAll(...args: any[]): IEntityActionSet<T, E>['editAll'];
    edit(...args: any[]): IEntityActionSet<T, E>['edit'];
    deleteAll(...args: any[]): IEntityActionSet<T, E>['deleteAll'];
    delete(...args: any[]): IEntityActionSet<T, E>['delete'];
    voidAll(...args: any[]): IEntityActionSet<T, E>['voidAll'];
    void(...args: any[]): IEntityActionSet<T, E>['void'];
}

export type EntityActionMap<T extends string, E extends Entity> = {
    loadAll?: NetworkAction<T, void, NetworkArray<E>>;
    load?: NetworkAction<T, void, E>;
    addAll?: NetworkAction<T, void, number[]>;
    add?: NetworkAction<T, void, number>;
    editAll?: NetworkAction<T, void, void>;
    edit?: NetworkAction<T, void, void>;
    deleteAll?: NetworkAction<T, void, void>;
    delete?: NetworkAction<T, void, void>;
    voidAll?: IAction<T, void>;
    void?: IAction<T, void>;
};

export type ProvidedKeys<M extends EntityActionMap<any, any>, KK extends string = keyof IEntityActionCreatorSet<any, any>> =
    { [K in KK]: M extends { [k in K]: any } ? K : never }[KK];
export type InferredActionType<M extends EntityActionMap<string, any>, K extends string> = M extends { [k in K]: infer A } ? ExtractType<A> : never

import rootEntityActionSet, { RootEntityMappedActionCreators } from './rootEntityActionSet';
import nestedEntityActionSet, { NestedEntityMappedActionCreators } from './nestedEntityActionSet';
import doubleNestedEntityActionSet, { DoubleNestedEntityMappedActionCreators } from './doubleNestedEntityActionSet';
import tripleNestedEntityActionSet, { TripleNestedEntityMappedActionCreators } from './tripleNestedEntityActionSet';

export default function entityActionSet<E extends Entity, M extends EntityActionMap<any, E>>(entity: E, map: M):
    RootEntityMappedActionCreators<E, M>;
export default function entityActionSet<P extends Entity, E extends Entity, M extends EntityActionMap<any, E>>(parent: P, entity: E, map: M):
    NestedEntityMappedActionCreators<P, E, M>;
export default function entityActionSet<G extends Entity, P extends Entity, E extends Entity, M extends EntityActionMap<any, E>>(grandParent: G, parent: P, entity: E, map: M):
    DoubleNestedEntityMappedActionCreators<G, P, E, M>;
export default function entityActionSet<GG extends Entity, G extends Entity, P extends Entity, E extends Entity, M extends EntityActionMap<any, E>>(grandGrandParent: GG, grandParent: G, parent: P, entity: E, map: M):
    TripleNestedEntityMappedActionCreators<GG, G, P, E, M>;
export default function entityActionSet(...args: any[]) {
    switch (true) {
        case args.length === 2:
            return rootEntityActionSet(args[0], args[1]);

        case args.length === 3:
            return nestedEntityActionSet(args[0], args[1], args[2]);

        case args.length === 4:
            return doubleNestedEntityActionSet(args[0], args[1], args[2], args[3]);

        case args.length === 5:
            return tripleNestedEntityActionSet(args[0], args[1], args[2], args[3], args[4]);

        default:
            throw new Error('Unexpected arguments.');
    }
}